import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cabinet } from '../../api/cabinet';
import { useSession } from '../../app/session';
import { useSite } from '../../app/site';
import { Alert } from '../../components/Alert';
import { BRAND } from '../../config';
import { useT } from '../../i18n';
import { describeError } from '../../lib/errors';
import { useAsync } from '../../lib/useAsync';
import { realLegalEntity, type LegalEntity } from './legal';

/** Save JSON as a file on the device. */
export function saveJson(data: unknown, fileName: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function PrivacyPage() {
  const t = useT();
  const navigate = useNavigate();
  const logout = useSession((s) => s.logout);
  const site = useSite((s) => s.site);
  const [exportState, setExportState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const [exportError, setExportError] = useState<unknown>(null);
  const [leaving, setLeaving] = useState(false);
  const me = useSession((s) => s.me);
  const [optOut, setOptOut] = useState<boolean>(!!me?.contact?.feedback_opt_out_at);
  // запити на відгук (батч p5-D): перемикач показуємо, лише коли бекенд їх уже має (GET /cabinet/feedback не 404)
  const feedbackOn = useAsync(async () => {
    try {
      const r = await cabinet.feedback();
      if (typeof r.opt_out === 'boolean') setOptOut(r.opt_out);
      return true;
    } catch {
      return false;
    }
  }, []);
  const [optState, setOptState] = useState<'idle' | 'busy' | 'saved' | 'error'>('idle');
  const [optError, setOptError] = useState<unknown>(null);

  const toggleOptOut = async (next: boolean) => {
    setOptOut(next);
    setOptState('busy');
    setOptError(null);
    try {
      const r = await cabinet.feedbackOptOut(next);
      if (typeof r?.opt_out === 'boolean') setOptOut(r.opt_out);
      setOptState('saved');
      const cur = useSession.getState().me;
      if (cur?.contact)
        useSession.setState({ me: { ...cur, contact: { ...cur.contact, feedback_opt_out_at: next ? new Date().toISOString() : null } } });
    } catch (e) {
      setOptOut(!next);
      setOptError(e);
      setOptState('error');
    }
  };

  // юрособа приходить з GET /public/site (може бути відсутня); заглушку на кшталт «ФОП (заповнити реквізити)»
  // і реквізити без податкового номера клієнту не показуємо
  const legal = realLegalEntity((site as { legal_entity?: LegalEntity | null } | null)?.legal_entity);
  const phone = site?.phone || site?.duty_phone || '';
  const duty = site?.duty_phone;
  const email = site?.email || '';
  const tel = (p: string) => `tel:${p.replace(/[^\d+]/g, '')}`;

  const doExport = async () => {
    setExportState('busy');
    setExportError(null);
    try {
      const data = await cabinet.exportData();
      saveJson(data, `${BRAND.toLowerCase().replace(/\W+/g, '-')}-my-data-${new Date().toISOString().slice(0, 10)}.json`);
      setExportState('done');
    } catch (e) {
      setExportError(e);
      setExportState('error');
    }
  };

  const doLogout = async () => {
    setLeaving(true);
    await logout();
    navigate('/login', { replace: true });
  };

  const rows = [
    phone ? { label: t('privacy.phone'), value: phone, href: tel(phone) } : null,
    duty && duty !== phone ? { label: t('privacy.duty'), value: duty, href: tel(duty) } : null,
    email ? { label: t('privacy.email'), value: email, href: `mailto:${email}` } : null,
    // окремої адреси для питань щодо даних немає — питання приймає загальна пошта
    email ? { label: t('privacy.dpo'), value: email, href: `mailto:${email}` } : null,
    legal?.name ? { label: t('privacy.company'), value: legal.name } : null,
    legal?.address ? { label: t('privacy.address'), value: legal.address } : null,
  ].filter((r): r is { label: string; value: string; href?: string } => r !== null);

  return (
    <>
      <h1 className="text-[28px] leading-tight sm:text-[34px]">{t('privacy.title')}</h1>
      <p className="mt-4 max-w-prose text-[16px] leading-relaxed text-txt">{t('privacy.intro')}</p>

      <div className="mt-12 flex flex-col gap-14">
        <section aria-labelledby="exp-h">
          <h2 id="exp-h" className="sec-h">
            {t('privacy.exportTitle')}
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-txt">{t('privacy.exportHint')}</p>
          <button type="button" className="btn mt-6 w-full sm:w-auto" onClick={() => void doExport()} disabled={exportState === 'busy'}>
            {exportState === 'busy' ? t('privacy.exporting') : t('privacy.export')}
          </button>
          <div className="mt-4" aria-live="polite">
            {exportState === 'done' ? <Alert kind="success">{t('privacy.exported')}</Alert> : null}
            {exportState === 'error' ? <Alert kind="error">{t(describeError(exportError))}</Alert> : null}
          </div>
          <p className="mt-4 text-[14px] leading-relaxed text-lab">{t('privacy.erase')}</p>
        </section>

        {feedbackOn.data ? (
          <section aria-labelledby="fb-h">
            <h2 id="fb-h" className="sec-h">
              {t('privacy.feedbackTitle')}
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-txt">{t('privacy.feedbackHint')}</p>
            <label className="mt-6 flex min-h-tap cursor-pointer items-start gap-3 text-[16px]">
              <input
                type="checkbox"
                role="switch"
                aria-checked={optOut}
                checked={optOut}
                disabled={optState === 'busy'}
                onChange={(e) => void toggleOptOut(e.target.checked)}
                className="mt-1 h-5 w-5 flex-none"
              />
              <span>{t('privacy.feedbackOptOut')}</span>
            </label>
            <div className="mt-3" aria-live="polite">
              {optState === 'saved' ? <Alert kind="success">{t('privacy.saved')}</Alert> : null}
              {optState === 'error' ? <Alert kind="error">{t(describeError(optError))}</Alert> : null}
            </div>
          </section>
        ) : null}

        {rows.length ? (
          <section aria-labelledby="ct-h">
            <h2 id="ct-h" className="sec-h">
              {t('privacy.contacts')}
            </h2>
            <dl className="mt-2">
              {rows.map((r) => (
                <div key={r.label} className="row flex flex-col gap-1 py-4 sm:flex-row sm:items-baseline sm:gap-6">
                  <dt className="flex-none text-[13px] text-lab sm:w-48">{r.label}</dt>
                  <dd className="text-[16px]">
                    {r.href ? (
                      <a href={r.href} className="inline-flex min-h-tap items-center underline decoration-line underline-offset-4 hover:decoration-fg">
                        {r.value}
                      </a>
                    ) : (
                      r.value
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        <section aria-labelledby="lo-h">
          <h2 id="lo-h" className="sec-h">
            {t('privacy.session')}
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-txt">{t('privacy.logoutHint')}</p>
          <button type="button" className="btn mt-6 w-full sm:w-auto" onClick={() => void doLogout()} disabled={leaving}>
            {leaving ? t('privacy.loggingOut') : t('privacy.logout')}
          </button>
        </section>
      </div>
    </>
  );
}
